import * as THREE from "three";
import { OBJLoader } from "three/addons/loaders/OBJLoader.js";
import { setOrbitControlsEnabled } from "../Camera";
// Custom rotation controls class
import { addEventListener } from "@/helpers/clickManager";
import {
  MovementTypes,
  moveToothBuccal,
  moveToothLingual,
  moveToothDown,
  moveToothUp,
  moveToothDistally,
  moveToothMesially,
  rotationN,
  rotationP,
  leftHingeN,
  leftHingeP,
  rightHingeN,
  rightHingeP,
  angulationN,
  angulationP,
  inclinationN,
  inclinationP,
  undoMovementList,
  iprOverlaysGlobalCopy,
  setIsMultipleTeethMovement,
  setStopAutoSave,
  publicCallForEachSelectedTooth,
  toothMovementTypes,
} from "../teethControls/TeethPositionHelper";
import { getCenter } from "../Elastics";
import { Vector3 } from "three";
import { globalData } from "../../../helpers/globData.ts";
import { Jaw } from "../interfaces.ts";
import { heatMapManager, isOrbitControlsAuthorized } from "../Mesh.jsx";
export default class CustomRotationControls {
  constructor() {
    this.camera = null;
    this.controls = null;
    this.domElement = window;
    this.object = null;
    this.setAutoSave = null;
    this.isRotating = false;
    this.selectedAxis = null;
    this.startMousePosOnAxis = new THREE.Vector2();
    this.startRotation = 0;
    this.rotationSpeed = 0.01;

    this.mainGroup = null;
    this.raycaster = new THREE.Raycaster();
    this.raycaster.layers.enable(1);
    this.raycaster.layers.enable(2);
    this.mouse = new THREE.Vector2();
    this.axes = []; // Holds x, y, z axis meshes
    this.selectedAttachmentInitialRotation = new THREE.Euler();
    this.selectedAttachment = null;
    this.up = new THREE.Vector3(0, 1, 0);
    this.forward = new THREE.Vector3(0, 0, 1);
    this.right = new THREE.Vector3(1, 0, 0);
    this.xAxis = null;
    this.yAxis = null;
    this.zAxis = null;
    this.verticalAxisRatio = 1 / 4;
    this.gizmosHoveredColor = "#FF0000";
    this.gizmosDefaultColor = "#77BBDA";
    this.gizmosMaterial = new THREE.MeshStandardMaterial({
      color: this.gizmosDefaultColor,
      depthTest: false,
      transparent: true,
    });

    // Bind event handlers to the class context
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.createAxes();
  }
  // Method to attach an object and add controls
  attach(object) {
    this.mainGroup.visible = true;
    if (object) {
      // Position and scale the axes
      this.update(object);
    }
    this.object = object;
    /* if (this.object.name === "_att") this.setRealCenter(); */
    // Create axes when an object is attached

    // Add event listeners for rotation
    this.addEventListeners();
  }

  // Method to detach the object and remove controls
  detach() {
    try {
      const groupToRemove = this.mainGroup;
      if (groupToRemove) {
        groupToRemove.removeFromParent();
        groupToRemove.visible = false;
      }
    } catch (err) {
      console.log(err);
    }
    this.object = null;
  }

  createAxes() {
    if (!this.mainGroup) {
      const curve = new THREE.QuadraticBezierCurve3(
        new THREE.Vector3(-2, 0, 0),
        new THREE.Vector3(0, 2, 0),
        new THREE.Vector3(2, 0, 0),
      );

      const createAxis = (color, name) => {
        const AxisGroup = new THREE.Group();

        const tubeGeometry = new THREE.TubeGeometry(curve, 20, 0.15, 8, false);
        const tubeMaterial = this.gizmosMaterial.clone();
        const tubeMesh = new THREE.Mesh(tubeGeometry, tubeMaterial);
        tubeMesh.name = name;
        AxisGroup.add(tubeMesh);

        const arrowHeadGeometry = new THREE.ConeGeometry(0.17, 0.6, 6);
        const arrowHeadMaterial = tubeMaterial;

        const arrowHead1 = new THREE.Mesh(arrowHeadGeometry, arrowHeadMaterial);
        arrowHead1.name = name;
        arrowHead1.position.set(-2.18, -0.21, 0);
        arrowHead1.rotation.z = Math.PI / 1.3;

        AxisGroup.add(arrowHead1);

        const arrowHead2 = new THREE.Mesh(arrowHeadGeometry, arrowHeadMaterial);
        arrowHead2.name = name;
        arrowHead2.position.set(2.18, -0.21, 0);
        arrowHead2.rotation.z = Math.PI / -1.3;
        AxisGroup.add(arrowHead2);
        const setAxisColor = (axisGroup, color) => {
          axisGroup.children.forEach(part => {
            part.material.color.set(color);
          });
        };

        // Add hover effect for each part of the axis group
        [tubeMesh, arrowHead1, arrowHead2].forEach(part => {
          addEventListener(
            part,
            "mouseover",
            () => {
              setAxisColor(AxisGroup, this.gizmosHoveredColor);
              this.isOn = true;
            },
            0,
          );

          addEventListener(
            part,
            "mouseout",
            () => {
              setAxisColor(AxisGroup, this.gizmosDefaultColor);
              this.isOn = false;
            },
            0,
          );
        });
        addEventListener(
          arrowHead1,
          "mousedown",
          e => {
            // console.log(e);
            this.onMouseDown(e.object);
          },
          0,
        );
        addEventListener(
          arrowHead2,
          "mousedown",
          e => {
            // console.log(e);
            this.onMouseDown(e.object);
          },
          0,
        );
        addEventListener(
          tubeMesh,
          "mousedown",
          e => {
            // console.log(e);
            this.onMouseDown(e.object);
          },
          0,
        );
        return AxisGroup;
      };
      // Create X, Y, Z axes
      this.xAxis = createAxis(this.gizmosDefaultColor, "zAxis");
      this.yAxis = createAxis(this.gizmosDefaultColor, "xAxis");
      this.zAxis = createAxis(this.gizmosDefaultColor, "yAxis");

      // addEventListener(yAxis, "mousedown", e => {
      //   if (yAxis.uuid === e) console.log(e, yAxis);
      // });
      // addEventListener(zAxis, "mousedown", e => {
      //   if (zAxis.uuid === e) console.log(e, zAxis);
      //   console.log(e, zAxis);
      // });
      const mainGroup = new THREE.Group();
      const sGroup = new THREE.Group();
      mainGroup.name = "Attachments ori group";
      sGroup.add(this.xAxis, this.yAxis, this.zAxis);
      sGroup.position.z += 0.9;
      mainGroup.add(sGroup);

      mainGroup.scale.set(1.2, 1.2, 1.2);

      this.mainGroup = mainGroup;

      this.axes = [this.xAxis, this.yAxis, this.zAxis];
    }
  }

  setCamera(camera) {
    this.camera = camera;
  }

  setScene(scene) {
    this.scene = scene;
  }

  setControls(controls) {
    this.controls = controls;
  }
  setRealCenter() {
    if (this.object.geometry && this.object.geometry.isBufferGeometry) {
      this.object.geometry.computeBoundingBox();
      const boundingBox = this.object.geometry.boundingBox;
      const center = boundingBox.getCenter(new THREE.Vector3());
      this.object.geometry.translate(-center.x, -center.y, -center.z);
      this.object.position.add(center);

      // Adjust children positions to maintain relative positions
      if (this.object.children)
        this.object.children.forEach(child => {
          child.position.sub(center);
        });
    }
  }
  // Remove axes from the scene when detaching or disposing
  removeAxes() {
    this.object.parent.remove(this.mainGroup);
  }
  attatchControls(controls) {
    this.controls = controls;
  }
  addEventListeners() {
    // this.domElement.addEventListener("mousedown", this.onMouseDown);
    this.domElement.addEventListener("mousemove", this.onMouseMove);
    this.domElement.addEventListener("mouseup", this.onMouseUp);
  }

  removeEventListeners() {
    // this.domElement.removeEventListener("mousedown", this.onMouseDown);
    this.domElement.removeEventListener("mousemove", this.onMouseMove);
    this.domElement.removeEventListener("mouseup", this.onMouseUp);
  }

  // Hide other axes when one is selected
  hideOtherAxes(selectedName) {
    this.axes.forEach(axis => {
      if (axis.children[0].name !== selectedName) {
        axis.visible = false;
      } else {
        axis.children.forEach(e => {
          e?.material?.color.set(this.gizmosHoveredColor);
        });
      }
    });
  }

  // Show all axes when mouse is released
  showAllAxes() {
    this.axes.forEach(axis => {
      axis.visible = true;
      axis.children.forEach(e => {
        e?.material?.color.set(this.gizmosDefaultColor);
      });
    });
  }

  onMouseDown(object) {
    if (object) {
      this.selectedAttachmentInitialRotation =
        this.object?.rotation?.clone() || new THREE.Euler();
      this.selectedAttachment = this.object;

      this.isRotating = true;
      this.startMousePosOnAxis.x = 0;
      this.startMousePosOnAxis.y = 0;
      // Select the axis based on object name
      if (object.name === "xAxis") {
        // this.controls.enabled = false;
        setOrbitControlsEnabled(false);
        this.selectedAxis = "x";
        this.startRotation = this.object?.rotation?.x || 0;
      } else if (object.name === "yAxis") {
        // this.controls.enabled = false;
        setOrbitControlsEnabled(false);
        this.selectedAxis = "y";
        this.startRotation = this.object?.rotation?.y || 0;
      } else if (object.name === "zAxis") {
        // this.controls.enabled = false;
        setOrbitControlsEnabled(false);
        this.selectedAxis = "z";
        this.startRotation = this.object?.rotation?.z || 0;
      }

      // Hide the other axes
      this.hideOtherAxes(object.name);
    }
  }

  onMouseMove(event) {
    if (this.isRotating && this.selectedAxis) {
      if (
        this.startMousePosOnAxis.x === 0 &&
        this.startMousePosOnAxis.y === 0
      ) {
        this.startMousePosOnAxis.x = event.clientX;
        this.startMousePosOnAxis.y = event.clientY;
        return;
      }
      let delta, deltaX, deltaY;

      deltaX = event.clientX - this.startMousePosOnAxis.x;
      deltaY = event.clientY - this.startMousePosOnAxis.y;

      delta =
        Math.abs(deltaX) > Math.abs(deltaY)
          ? deltaX
          : -deltaY * this.verticalAxisRatio;
      // Apply rotation based on selected axis

      if (this.selectedAxis === "x") {
        this.object.rotateOnAxis(this.right, delta * this.rotationSpeed);
      } else if (this.selectedAxis === "y") {
        this.object.rotateOnAxis(this.up, delta * this.rotationSpeed);
      } else if (this.selectedAxis === "z") {
        this.object.rotateOnAxis(this.forward, delta * this.rotationSpeed);
      }
      this.startMousePosOnAxis.x = event.clientX;
      this.startMousePosOnAxis.y = event.clientY;
    }
  }
  isHovered() {
    return this.isOn;
  }
  onMouseUp() {
    // this.controls.enabled = true;

    // save undo withOld rotation

    if (this.selectedAttachment) {
      if (
        this.selectedAttachment.rotation !=
          this.selectedAttachmentInitialRotation &&
        this.isRotating
      ) {
        this.registerToUndo();
        this.update(this.object);
        if (this.setAutoSave) {
          this.setAutoSave(true);
        }
      }
    }
    // setOrbitControlsEnabled(true);

    this.isRotating = false;
    this.selectedAxis = null;

    // Show all axes again when mouse is released
    this.showAllAxes();
  }
  update(object) {
    if (object) {
      this.xAxis.position.y = 0.85;
      this.yAxis.rotation.x = 1.57;
      this.yAxis.rotation.y = 1.57 - object.rotation.z;
      this.yAxis.scale.set(0.8, 0.8, 0.8);
      this.zAxis.rotation.x = 1.57;
      this.zAxis.rotation.y = -object.rotation.z;
      this.zAxis.position.z = -0.1;
      this.zAxis.scale.set(0.8, 0.8, 0.8);

      // Add axes to the scene and store them for later removal
      this.mainGroup.position.copy(object.position);
      this.mainGroup.rotation.copy(object.rotation);
      this.mainGroup.rotation.y += Math.PI;
      this.mainGroup.rotation.z = Math.PI;

      object.parent.add(this.mainGroup);
    }
  }
  // Cleanup method to remove everything
  dispose() {
    this.detach();
  }

  registerToUndo() {
    undoMovementList.push({
      action: "rotate",
      actionType: MovementTypes.ATTACHMENTS,
      attachmentID: this.object.customID,
      attachmentName: this.object.name,
      rotation: this.selectedAttachmentInitialRotation,
      tooth: this.object?.parent?.parent,
    });
  }
}

export class GizmoControl {
  static domElement;
  static perspectiveCamera;
  static orthographicCamera;
  activeCamera;

  logs;
  setLogs;
  setIprOverlays;
  setAutoSave;
  isMouseOver = false;
  isEnabled = false;
  isActionDone = false;
  objects = [];
  straightArrowGeometry;
  bendedArrowGeometry;
  clickableBendedArrowGeometry;
  gizmoTextureMap;
  startRotation;
  ACTIONS = {
    x_pos: "x_pos",
    y_pos: "y_pos",
    z_pos: "z_pos",
    x_rot: "x_rot",
    y_rot: "y_rot",
    z_rot: "z_rot",
    y_leftrot: "y_leftrot",
    y_rightrot: "y_rightrot",
  };
  translatingMovementType = [
    MovementTypes.MESIALDISTAL,
    MovementTypes.BUCCALLINGUAL,
    MovementTypes.EXTRUSIONINTRUSION,
  ];
  cameraIndex = 0;
  negCounter = 0;
  posCounter = 0;
  actionSmoother = 3;
  actionSmootherBuccalLingual = 2;
  clickScaleFactor;
  currentAction = this.ACTIONS.x_pos;
  currentMovementType = MovementTypes.EXTRUSIONINTRUSION;
  displayedGizmoMaterial;
  clickableGizmoMaterial;
  constructor() {
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
    this.dragging = false;
    this.dragAxis = null;
    this.startPoint = new THREE.Vector3();
    this.offset = new THREE.Vector3();
    this.dragPlane = null;
    this.planeHelper = null;
    this.clickScaleFactor = new Vector3(1.5, 1.5, 1.5);

    this.startRotation = new THREE.Quaternion();

    this.setActiveCameraIndex(0);
    // this.isEnabled = true;
    /* this.movements = {
      leftHinge,
      rightHinge,
      angulation,
      inclination,
      rotation,
      up,
      right,
      buccal,
      leftHingeCounter,
      rightHingeCounter,
      angulationCounter,
      inclinationCounter,
      rotationCounter,
      down,
      left,
      lingual,
    }; */

    const loader = new OBJLoader();

    loader.load(
      // resource URL
      "Straight_Arrow_up.obj",
      // called when resource is loaded
      function (o) {
        this.straightArrowGeometry = o.children[0].geometry.clone();
      }.bind(this),
      // called when loading is in progress
      function (xhr) {},
      // called when loading has errors
      function (error) {
        console.log("An error happened", error);
      },
    );

    loader.load(
      // resource URL
      "Bended_Arrow.obj",
      // called when resource is loaded
      function (o) {
        this.bendedArrowGeometry = o.children[0].geometry.clone();
      }.bind(this),
      // called when loading is in progress
      function (xhr) {},
      // called when loading has errors
      function (error) {
        console.log("An error happened", error);
      },
    );

    loader.load(
      // resource URL
      "Bended_Arrow_Clickable.obj",
      // called when resource is loaded
      function (o) {
        this.clickableBendedArrowGeometry = o.children[0].geometry.clone();
      }.bind(this),
      // called when loading is in progress
      function (xhr) {},
      // called when loading has errors
      function (error) {
        console.log("An error happened", error);
      },
    );

    const textureLoader = new THREE.TextureLoader();

    // Load the PNG file
    this.gizmoTextureMap = textureLoader.load("GizmoArrowsTexture.png");

    this.displayedGizmoMaterial = new THREE.MeshStandardMaterial({
      depthTest: false,
      depthWrite: false,
      color: 0xffffff,
      map: this.gizmoTextureMap,
      flatShading: false,
    });

    this.clickableGizmoMaterial = new THREE.MeshStandardMaterial({
      visible: false,
      color: 0x000000,
    });

    this.axes = {
      X: new THREE.Vector3(1, 0, 0),
      Y: new THREE.Vector3(0, 1, 0),
      Z: new THREE.Vector3(0, 0, 1),
    };
  }
  /**
   *
   * @param {number} index 0 = orthographic, else = perspective
   */
  setActiveCameraIndex(index) {
    this.activeCamera = index
      ? GizmoControl.perspectiveCamera
      : GizmoControl.orthographicCamera;
  }
  attachTo(
    object,
    axes = {
      X: new THREE.Vector3(1, 0, 0),
      Y: new THREE.Vector3(0, 1, 0),
      Z: new THREE.Vector3(0, 0, 1),
    },
  ) {
    this.setObjects(object);

    this.object = object;

    this.axes = axes;
    if (!this.gizmoGroup) {
      this.scene = object.parent.parent.parent.parent.parent;
      this.createGizmos();
      this.addEventListeners();
      this.setMovementType(this.currentMovementType);
    } else {
      this.setMovementType(this.currentMovementType);
      /* this.centerGizmo();
      this.rotateGizmo(); */
    }
  }

  createGizmos() {
    this.gizmoGroup = new THREE.Group();
    this.gizmoGroup.visible = this.isEnabled;
    this.gizmoGroup.name = "gizmoGroup";
    this.scene.add(this.gizmoGroup);

    // const center = getCenter(this.object);
    // this.gizmoGroup.position.copy(center);

    this.gizmos = {
      Movement: this.createStarightGizmo(this.axes.X, 0xffffff, "x"),
      Rotation: this.createBendedGizmo(this.axes.X, 0xffffff, "x"),
    };

    for (let key in this.gizmos) {
      addEventListener(
        this.gizmos[key].children[0],
        "mousedown",
        data => {
          this.onPointerDown(data);
        },
        0,
      );
      /* addEventListener(
        this.dragPlane,
        "mousemove",
        (data, data2) => {
          console.log(data, data2);

          this.onPointerMove(data);
          console.log("move");
        },
        0,
      );
      */
      addEventListener(
        null,
        "mouseup",
        () => {
          this.onPointerUp();
        },
        0,
      );

      addEventListener(
        this.gizmos[key],
        "mouseover",
        () => {
          this.mouseOver = true;
        },
        0,
      );
      addEventListener(
        this.gizmos[key],
        "mouseout",
        () => {
          this.isMouseOver = false;
        },
        0,
      );
      this.gizmos[key].position.set(0, 0, 0);
      this.gizmoGroup.add(this.gizmos[key]);
    }
    /* addEventListener(null, "keydown", k => {
      const key = parseInt(k.key, 10);
      if (isNaN(key)) {
        return;
      }

      switch (k.key) {
        case "1":
          setToggle("selectedMovement", MovementTypes.MESIALDISTAL);
          this.setMovementType(MovementTypes.MESIALDISTAL);
          break;
        case "2":
          setToggle("selectedMovement", MovementTypes.EXTRUSIONINTRUSION);
          this.setMovementType(MovementTypes.EXTRUSIONINTRUSION);
          break;
        case "3":
          setToggle("selectedMovement", MovementTypes.BUCCALLINGUAL);
          this.setMovementType(MovementTypes.BUCCALLINGUAL);
          break;
        case "4":
          setToggle("selectedMovement", MovementTypes.ROTATION);
          this.setMovementType(MovementTypes.ROTATION);

          break;
        case "5":
          setToggle("selectedMovement", MovementTypes.INCLINATION);
          this.setMovementType(MovementTypes.INCLINATION);
          break;
        case "6":
          setToggle("selectedMovement", MovementTypes.ANGULATION);
          this.setMovementType(MovementTypes.ANGULATION);
          break;
        case "7":
          setToggle("selectedMovement", MovementTypes.LEFTHINGE);
          this.setMovementType(MovementTypes.LEFTHINGE);
          break;
        case "8":
          setToggle("selectedMovement", MovementTypes.RIGHTHINGE);
          this.setMovementType(MovementTypes.RIGHTHINGE);
          break;
      }
    }); */
  }

  setIsEnabled(state) {
    state = state && isOrbitControlsAuthorized;
    this.isEnabled = state;

    if (!this.object && !this.gizmoGroup) {
      return;
    }

    if (state) {
      if (this.object?.userData?.jaw === Jaw.Lower) {
        this.gizmoGroup.visible = globalData.isActive.showLower;
      } else {
        this.gizmoGroup.visible = globalData.isActive.showUpper;
      }
    } else {
      this.gizmoGroup.visible = state;
    }
  }
  setMovementType(type) {
    if (!this.object) {
      return;
    }

    switch (type) {
      case MovementTypes.MESIALDISTAL:
        this.currentAction = this.ACTIONS.x_pos;
        this.setGizmoState(type); // M/D
        this.gizmoGroup.children[0].visible = true;
        this.gizmoGroup.children[1].visible = false;
        this.centerGizmo();
        this.setGizmoAxis(this.object.userData["xAxis"]);
        this.currentMovementType = type;
        break;
      case MovementTypes.EXTRUSIONINTRUSION:
        this.currentAction = this.ACTIONS.y_pos;
        this.setGizmoState(type); // B/L
        this.gizmoGroup.children[0].visible = true;
        this.gizmoGroup.children[1].visible = false;
        this.centerGizmo();
        this.setGizmoAxis(this.object.userData["yAxis"]);
        this.currentMovementType = type;
        break;
      case MovementTypes.BUCCALLINGUAL:
        this.currentAction = this.ACTIONS.z_pos;
        this.setGizmoState(type); // In/Ex
        this.gizmoGroup.children[0].visible = true;
        this.gizmoGroup.children[1].visible = false;
        this.centerGizmo();
        this.setGizmoAxis(this.object.userData["zAxis"]);
        this.currentMovementType = type;
        break;
      case MovementTypes.ROTATION:
        this.currentAction = this.ACTIONS.y_rot;
        this.setGizmoState(type); // Rotation
        this.gizmoGroup.children[0].visible = false;
        this.gizmoGroup.children[1].visible = true;
        this.setGizmoAxis(
          this.object.userData["yAxis"].clone().multiplyScalar(-1),
        );
        this.centerGizmo();
        this.lookAtWithRight(
          this.object.userData["yAxis"],
          this.object.userData["zAxis"],
        );
        this.currentMovementType = type;
        break;
      case MovementTypes.INCLINATION:
        this.currentAction = this.ACTIONS.x_rot;
        this.setGizmoState(type); // Inclination
        this.gizmoGroup.children[0].visible = false;
        this.gizmoGroup.children[1].visible = true;
        this.setGizmoCenter(this.object.userData["center"]);
        this.setGizmoAxis(this.object.userData["xAxis"]);
        this.lookAtWithRight(
          this.object.userData["xAxis"],
          this.object.userData["yAxis"].clone().multiplyScalar(-1),
        );
        this.currentMovementType = type;
        break;
      case MovementTypes.ANGULATION:
        this.currentAction = this.ACTIONS.z_rot;
        this.setGizmoState(type); // Angulation
        this.gizmoGroup.children[0].visible = false;
        this.gizmoGroup.children[1].visible = true;
        this.centerGizmo();
        this.setGizmoAxis(
          this.object.userData["zAxis"].clone().multiplyScalar(-1),
        );
        this.lookAtWithRight(
          this.object.userData["zAxis"],
          this.object.userData["yAxis"].clone().multiplyScalar(-1),
        );
        this.currentMovementType = type;
        break;
      case MovementTypes.LEFTHINGE:
        this.currentAction = this.ACTIONS.y_leftrot;
        this.setGizmoState(type); // Left Hinge
        this.gizmoGroup.children[0].visible = false;
        this.gizmoGroup.children[1].visible = true;
        this.setGizmoCenter(this.object.userData["leftHinge"]);
        this.setGizmoAxis(
          this.object.userData["yAxis"].clone().multiplyScalar(-1),
        );
        this.lookAtWithRight(
          this.object.userData["yAxis"],
          this.object.userData["leftHinge"]
            .clone()
            .sub(this.object.userData["center"])
            .normalize(),
        );
        this.currentMovementType = type;
        break;
      case MovementTypes.RIGHTHINGE:
        this.currentAction = this.ACTIONS.y_rightrot;
        this.setGizmoState(type); // Right Hinge
        this.gizmoGroup.children[0].visible = false;
        this.gizmoGroup.children[1].visible = true;
        this.setGizmoCenter(this.object.userData["rightHinge"]);
        this.setGizmoAxis(
          this.object.userData["yAxis"].clone().multiplyScalar(-1),
        );

        this.lookAtWithRight(
          this.object.userData["yAxis"],
          this.object.userData["rightHinge"]
            .clone()
            .sub(this.object.userData["center"])
            .normalize(),
        );
        this.currentMovementType = type;
        break;
    }
  }
  setGizmoCenter(v) {
    this.gizmoGroup.position.copy(v);
    // this.gizmoGroup.children[0].position.set(0, 0, 0);
    // this.gizmoGroup.children[1].position.set(0, 0, 0);
  }

  _gizmoWorldPos = new Vector3();
  setGizmoAxis(v) {
    this.gizmoGroup.getWorldPosition(this._gizmoWorldPos);
    this.gizmoGroup.lookAt(this._gizmoWorldPos.clone().add(v));

    for (let key in this.gizmos) {
      this.gizmos[key].userData["axis"] = v;
    }
  }

  setGizmoState(state) {
    // console.log(state);
  }
  isHovered() {
    return this.isMouseOver;
  }
  centerGizmo() {
    let point;
    if (this.currentAction === this.ACTIONS.y_leftrot) {
      point = this.object.userData["leftHinge"];
    } else if (this.currentAction === this.ACTIONS.y_rightrot) {
      point = this.object.userData["rightHinge"];
    } else {
      point = this.object.userData["center"];
    }
    this.setGizmoCenter(
      /* getCenter(this.object) */ /* this.object.userData["center"] */ point,
    );
  }
  /**
   * Rotates an object to align with a specific forward and right vector.
   *
   * @param {THREE.Vector3} forwardVector The desired world-space forward direction.
   * @param {THREE.Vector3} rightVector The desired world-space right direction.
   */
  lookAtWithRight(forwardVector, rightVector) {
    const rotationMatrix = new THREE.Matrix4();

    // Step 1: Normalize the input vectors to ensure they are pure directions
    const newForward = forwardVector.clone().normalize();
    const newRight = rightVector.clone().normalize();

    // Step 2: Calculate the new 'Up' vector using the cross product.
    // The order (right x forward) is crucial for a right-handed coordinate system.
    const newUp = new THREE.Vector3().crossVectors(newRight, newForward);

    // Step 3 (Optional but recommended): Re-calculate the 'Right' vector.
    // This makes the function robust, ensuring the three vectors are perfectly orthogonal
    // even if the original inputs weren't.
    newRight.crossVectors(newUp, newForward).negate(); // Negate to correct direction

    // Step 4: Create the rotation matrix from the three basis vectors.
    // In Three.js, an this.object's "front" is its local -Z axis.
    // So we use the negated forward vector for the Z-basis.
    rotationMatrix.makeBasis(newRight, newUp, newForward.clone().negate());

    // Step 5: Apply the rotation to the object's quaternion.
    this.gizmoGroup.quaternion.setFromRotationMatrix(rotationMatrix);
  }
  rotateGizmo() {
    return;
    let axis;
    for (let axisIndex = 0; axisIndex < 3; axisIndex++) {
      const object = this.gizmoGroup.children[axisIndex];
      if (axisIndex === 0) {
        axis = this.getPerpendicularXZ(this.axes.Z);
        object.userData.axis = axis;
      } else if (axisIndex === 1) {
        axis = this.axes.Y;
        object.userData.axis = axis;
      } else {
        axis = this.axes.Z;
        object.userData.axis = axis;
      }
      const defaultAxis = new THREE.Vector3(0, 1, 0);
      const targetAxis = axis.clone().normalize();

      // Create a quaternion that rotates from defaultAxis to targetAxis
      const quaternion = new THREE.Quaternion();
      quaternion.setFromUnitVectors(defaultAxis, targetAxis);
      object.quaternion.copy(quaternion);
    }
  }
  createGizmo(axis, color, name) {
    const material = new THREE.MeshStandardMaterial({
      depthTest: false,
      color,
      transparent: true,
      opacity: 0.7,
    });
    const geometry = new THREE.CylinderGeometry(0.5, 0.5, 20, 12);
    const gizmo = new THREE.Mesh(geometry, material);

    // Align cylinder to the axis
    const defaultAxis = new THREE.Vector3(0, 1, 0);
    const targetAxis = axis.clone().normalize();

    // Create a quaternion that rotates from defaultAxis to targetAxis
    const quaternion = new THREE.Quaternion();
    quaternion.setFromUnitVectors(defaultAxis, targetAxis);
    gizmo.quaternion.copy(quaternion);

    gizmo.position.copy(axis.clone().multiplyScalar(1.2));
    gizmo.userData.axis = axis.clone();
    gizmo.userData.axisName = name;
    gizmo.name = name;
    return gizmo;
  }

  createStarightGizmo(axis, color, name) {
    const geometry = this.straightArrowGeometry;
    geometry.computeVertexNormals();
    const gizmo = new THREE.Mesh(geometry, this.displayedGizmoMaterial);
    const gizmoChild = new THREE.Mesh(geometry, this.clickableGizmoMaterial); // for raycast detecting
    gizmoChild.scale.copy(new Vector3(2, 2, 1.2));
    gizmo.attach(gizmoChild);

    // Align cylinder to the axis
    // const defaultAxis = new THREE.Vector3(0, 1, 0);
    // const targetAxis = axis.clone().normalize();

    // Create a quaternion that rotates from defaultAxis to targetAxis
    // const quaternion = new THREE.Quaternion();
    // quaternion.setFromUnitVectors(defaultAxis, targetAxis);
    // gizmo.quaternion.copy(quaternion);

    // gizmo.position.copy(axis.clone().multiplyScalar(1.2));
    gizmo.userData.axis = axis.clone();
    gizmo.userData.axisName = name;
    gizmo.name = name;
    gizmoChild.name = name;

    return gizmo;
  }

  createBendedGizmo(axis, color, name) {
    const geometry = this.bendedArrowGeometry;
    geometry.computeVertexNormals();
    const gizmo = new THREE.Mesh(geometry, this.displayedGizmoMaterial);
    const scaledGeom = this.clickableBendedArrowGeometry;
    const gizmoChild = new THREE.Mesh(scaledGeom, this.clickableGizmoMaterial); // for raycast detecting

    gizmo.attach(gizmoChild);

    // Align cylinder to the axis
    // const defaultAxis = new THREE.Vector3(0, 1, 0);
    // const targetAxis = axis.clone().normalize();

    // Create a quaternion that rotates from defaultAxis to targetAxis
    // const quaternion = new THREE.Quaternion();
    // quaternion.setFromUnitVectors(defaultAxis, targetAxis);
    // gizmo.quaternion.copy(quaternion);

    gizmo.rotation.y = Math.PI;

    // gizmo.position.copy(axis.clone().multiplyScalar(1.2));
    gizmo.userData.axis = axis.clone();
    gizmo.userData.axisName = name;
    gizmo.name = name;
    gizmoChild.name = name;
    return gizmo;
  }

  addEventListeners() {
    // Listen on a broader scope for pointer down
    // window.addEventListener("mousedown", this.onPointerDown.bind(this));
    // Pointer move and up on the domElement
    GizmoControl.domElement.addEventListener(
      "pointermove",
      this.onPointerMove.bind(this),
    );
    // GizmoControl.domElement.addEventListener(
    //   "pointerup",
    //   this.onPointerUp.bind(this),
    // );
  }
  updateMouseCoords(event) {
    const rect = GizmoControl.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  }
  getPlaneNormalForDragAxis(dragAxis) {
    // If dragging along Y (vertical), choose a horizontal normal (e.g., along Z)
    /* if (Math.abs(dragAxis.y) === 1) {
      return new THREE.Vector3(0, 0, 1);
    } */
    // For other axes, use the camera's direction to compute a plane normal.
    const cameraDirection = new THREE.Vector3();

    this.activeCamera.getWorldDirection(cameraDirection);
    // Compute an intermediate vector that is orthogonal to both the drag axis and the camera.
    const intermediate = new THREE.Vector3()
      .crossVectors(dragAxis, cameraDirection)
      .normalize();
    // Compute the plane normal which is perpendicular to the drag axis and the intermediate vector.
    return new THREE.Vector3().crossVectors(dragAxis, intermediate).normalize();
  }

  functionNameList = {
    x_pos: { pos: this.left.bind(this), neg: this.right.bind(this) },
    y_pos: { pos: this.up.bind(this), neg: this.down.bind(this) },
    z_pos: { pos: this.lingual.bind(this), neg: this.buccal.bind(this) },
    x_rot: {
      pos: this.inclination.bind(this),
      neg: this.inclinationCounter.bind(this),
    },
    y_rot: {
      pos: this.rotation.bind(this),
      neg: this.rotationCounter.bind(this),
    },
    z_rot: {
      pos: this.angulation.bind(this),
      neg: this.angulationCounter.bind(this),
    },
    y_leftrot: {
      pos: this.leftHinge.bind(this),
      neg: this.leftHingeCounter.bind(this),
    },
    y_rightrot: {
      pos: this.rightHinge.bind(this),
      neg: this.rightHingeCounter.bind(this),
    },
  };

  onPointerDown(event) {
    if (!this.object || !event || !this.isEnabled) {
      return;
    }

    setOrbitControlsEnabled(false);
    const selectedGizmo = event.object.parent;

    this.dragging = true;
    this.dragAxis = selectedGizmo.userData.axis.clone();

    this.dragAxisName = this.currentAction;
    this.startPoint.copy(event.point);
    this.offset.copy(this.object.position);

    // Create a plane that’s perpendicular to the drag axis and passes through the start point.
    this.dragPlane = new THREE.Plane();
    let planeNormal = this.activeCamera.position
      .clone()
      .sub(this.startPoint)
      .normalize();
    this.dragPlane.setFromNormalAndCoplanarPoint(planeNormal, this.startPoint);
    // Here we use the drag axis as the normal. You can adjust this if needed.
    /* if (isMoving) {
      planeNormal = this.getPlaneNormalForDragAxis(
        this.activeCamera.getWorldDirection(new THREE.Vector3()),
      );
      this.dragPlane.setFromNormalAndCoplanarPoint(
        planeNormal,
        this.startPoint,
      );
    } else {
      planeNormal = this.getPlaneNormalForDragAxis(this.dragAxis);
      this.dragPlane.setFromNormalAndCoplanarPoint(
        planeNormal,
        this.startPoint,
      );
    }
 */

    this.startRotation.copy(this.object.quaternion);
    // Optionally add a visual helper for the plane (for debugging)
    if (this.planeHelper) {
      this.scene.remove(this.planeHelper);
    }
    this.planeHelper = new THREE.PlaneHelper(this.dragPlane, 100, 0xffff00);
    this.planeHelper.visible = false;
    this.scene.attach(this.planeHelper);
  }

  onPointerMove(event) {
    if (
      !this.object ||
      !this.dragging ||
      !this.dragAxis ||
      !this.dragPlane ||
      !this.isEnabled ||
      !event
    ) {
      return;
    }

    this.updateMouseCoords(event);
    this.raycaster.setFromCamera(this.mouse, this.activeCamera);
    const isMoving = this.translatingMovementType.includes(
      this.currentMovementType,
    );

    const intersectionPoint = new THREE.Vector3();
    if (!this.raycaster.ray.intersectPlane(this.dragPlane, intersectionPoint)) {
      return;
    }
    let dotInstant;
    if (isMoving) {
      const delta = this.startPoint.clone().sub(intersectionPoint);
      dotInstant = delta.dot(this.dragAxis);
      if (
        this.currentAction === this.ACTIONS.y_pos &&
        this.object.userData["jaw"] === Jaw.Lower
      ) {
        dotInstant *= -1;
      }
      this.startPoint.copy(intersectionPoint);
    } else {
      const angleToStart = this.startPoint
        .clone()
        .sub(getCenter(this.object))
        .normalize();
      const angleToCurrent = intersectionPoint
        .clone()
        .sub(getCenter(this.object))
        .normalize();

      /* let angle = Math.acos(
        THREE.MathUtils.clamp(angleToStart.dot(angleToCurrent), -1, 1),
      ); */
      const cross = new THREE.Vector3().crossVectors(
        angleToStart,
        angleToCurrent,
      );
      dotInstant = cross.dot(this.dragAxis);
      if (
        this.currentAction === this.ACTIONS.x_rot &&
        this.object.userData["jaw"] === Jaw.Lower
      ) {
        dotInstant *= -1;
      }
      /* if (dotInstant < 0) {
        angle = -angle;
      } */
      /* const rotationQuat = new THREE.Quaternion().setFromAxisAngle(
        this.dragAxis,
        angle,
      );
      this.object.quaternion.multiplyQuaternions(
        rotationQuat,
        this.startRotation,
      ); */

      this.startPoint.copy(intersectionPoint);
      this.startRotation.copy(this.object.quaternion);
    }
    // Compute movement relative to the start point
    let instantaneousDelta = new THREE.Vector3();

    if (this.previousIntersectionPoint) {
      instantaneousDelta
        .copy(intersectionPoint)
        .sub(this.previousIntersectionPoint);
    }
    // Update the previous point for next frame
    this.previousIntersectionPoint = intersectionPoint.clone();

    // Calculate instantaneous movement along the drag axis
    // const normalizedAxis = this.dragAxis.clone().normalize();
    //  dotInstant = instantaneousDelta.dot(normalizedAxis);

    const posName = this.functionNameList[this.dragAxisName];

    const isMultipleTeeth = this.objects.length > 1;
    if (isMultipleTeeth) {
      setIsMultipleTeethMovement(true);
      setStopAutoSave(true);
    }

    if (dotInstant > 0) {
      this.negCounter = 0;
      this.posCounter++;
    } else {
      this.posCounter = 0;
      this.negCounter++;
    }
    const initialObjectRef = this.object;

    for (let toothIndex = 0; toothIndex < this.objects.length; toothIndex++) {
      this.object = this.objects[toothIndex];
      if (dotInstant > 0) {
        if (
          this.posCounter === this.actionSmoother ||
          (this.currentAction === this.ACTIONS.z_pos &&
            this.posCounter === this.actionSmootherBuccalLingual)
        ) {
          posName.pos();
        }
      } else if (dotInstant < 0) {
        if (
          this.negCounter === this.actionSmoother ||
          (this.currentAction === this.ACTIONS.z_pos &&
            this.negCounter === this.actionSmootherBuccalLingual)
        ) {
          posName.neg();
        }
      } else {
        console.log("No instantaneous movement along the axis");
      }
      if (isMultipleTeeth && toothIndex === this.objects.length - 2) {
        setStopAutoSave(false);
      }
    }
    this.object = initialObjectRef;
    if (this.posCounter === this.actionSmoother) {
      this.posCounter = 0;
    }
    if (this.negCounter === this.actionSmoother) {
      this.negCounter = 0;
    }
    if (isMultipleTeeth) {
      setIsMultipleTeethMovement(false);
    }
    this.isActionDone = true;
    this.centerGizmo();
  }
  //#region movement calls
  leftHinge() {
    publicCallForEachSelectedTooth(
      this.object,
      this.logs,
      iprOverlaysGlobalCopy || [],
      this.setLogs,
      this.setIprOverlays,
      this.setAutoSave,
      leftHingeN,
    );
  }
  leftHingeCounter() {
    publicCallForEachSelectedTooth(
      this.object,
      this.logs,
      iprOverlaysGlobalCopy || [],
      this.setLogs,
      this.setIprOverlays,
      this.setAutoSave,
      leftHingeP,
    );
  }
  rightHinge() {
    publicCallForEachSelectedTooth(
      this.object,
      this.logs,
      iprOverlaysGlobalCopy || [],
      this.setLogs,
      this.setIprOverlays,
      this.setAutoSave,
      rightHingeN,
    );
  }
  rightHingeCounter() {
    publicCallForEachSelectedTooth(
      this.object,
      this.logs,
      iprOverlaysGlobalCopy || [],
      this.setLogs,
      this.setIprOverlays,
      this.setAutoSave,
      rightHingeP,
    );
  }

  angulation() {
    publicCallForEachSelectedTooth(
      this.object,
      this.logs,
      iprOverlaysGlobalCopy || [],
      this.setLogs,
      this.setIprOverlays,
      this.setAutoSave,
      angulationN,
    );
  }
  angulationCounter() {
    publicCallForEachSelectedTooth(
      this.object,
      this.logs,
      iprOverlaysGlobalCopy || [],
      this.setLogs,
      this.setIprOverlays,
      this.setAutoSave,
      angulationP,
    );
  }

  inclination() {
    publicCallForEachSelectedTooth(
      this.object,
      this.logs,
      iprOverlaysGlobalCopy || [],
      this.setLogs,
      this.setIprOverlays,
      this.setAutoSave,
      inclinationN,
    );
  }
  inclinationCounter() {
    publicCallForEachSelectedTooth(
      this.object,
      this.logs,
      iprOverlaysGlobalCopy || [],
      this.setLogs,
      this.setIprOverlays,
      this.setAutoSave,
      inclinationP,
    );
  }

  rotation() {
    publicCallForEachSelectedTooth(
      this.object,
      this.logs,
      iprOverlaysGlobalCopy || [],
      this.setLogs,
      this.setIprOverlays,
      this.setAutoSave,
      rotationN,
    );
  }

  rotationCounter() {
    publicCallForEachSelectedTooth(
      this.object,
      this.logs,
      iprOverlaysGlobalCopy || [],
      this.setLogs,
      this.setIprOverlays,
      this.setAutoSave,
      rotationP,
    );
  }

  up() {
    publicCallForEachSelectedTooth(
      this.object,
      this.logs,
      iprOverlaysGlobalCopy || [],
      this.setLogs,
      this.setIprOverlays,
      this.setAutoSave,
      moveToothUp,
    );
  }
  down() {
    publicCallForEachSelectedTooth(
      this.object,
      this.logs,
      iprOverlaysGlobalCopy || [],
      this.setLogs,
      this.setIprOverlays,
      this.setAutoSave,
      moveToothDown,
    );
  }
  buccal() {
    publicCallForEachSelectedTooth(
      this.object,
      this.logs,
      iprOverlaysGlobalCopy || [],
      this.setLogs,
      this.setIprOverlays,
      this.setAutoSave,
      moveToothBuccal,
    );
  }
  lingual() {
    publicCallForEachSelectedTooth(
      this.object,
      this.logs,
      iprOverlaysGlobalCopy || [],
      this.setLogs,
      this.setIprOverlays,
      this.setAutoSave,
      moveToothLingual,
    );
  }
  left() {
    const functionCall =
      (this.object.userData.jaw === Jaw.Lower) !==
      this.object.userData.center.x > 0
        ? moveToothDistally
        : moveToothMesially;
    publicCallForEachSelectedTooth(
      this.object,
      this.logs,
      iprOverlaysGlobalCopy || [],
      this.setLogs,
      this.setIprOverlays,
      this.setAutoSave,
      functionCall,
    );
  }
  right() {
    const functionCall =
      (this.object.userData.jaw === Jaw.Lower) !==
      this.object.userData.center.x < 0
        ? moveToothDistally
        : moveToothMesially;
    publicCallForEachSelectedTooth(
      this.object,
      this.logs,
      iprOverlaysGlobalCopy || [],
      this.setLogs,
      this.setIprOverlays,
      this.setAutoSave,
      functionCall,
    );
  }

  isInLeftSide(object) {
    const centerPoint = getCenter(object);
    return centerPoint.x < 0;
  }

  prepare3DControlsMovement(logs, setAutoSave, setLogs, setIprOverlays) {
    this.logs = logs;
    this.setAutoSave = setAutoSave;
    this.setLogs = setLogs;
    this.setIprOverlays = setIprOverlays;
  }
  //#endregion

  onPointerUp() {
    this.dragging = false;
    this.dragAxis = null;
    this.dragPlane = null;
    // Remove the helper if it exists
    if (this.planeHelper) {
      this.scene.remove(this.planeHelper);
      this.planeHelper = null;
    }
    if (this.isActionDone) {
      if (heatMapManager) {
        heatMapManager.clearTimeout();
        if (this.objects.length > 1) {
          const isLower = this.objects[0].userData["jaw"] === Jaw.Lower;

          for (
            let toothIndex = 0;
            toothIndex < this.objects.length;
            toothIndex++
          ) {
            let upper = isLower
                ? this.objects[0].parent.parent.children[0].children
                : [this.objects[toothIndex]],
              lower = isLower
                ? [this.objects[toothIndex]]
                : this.objects[0].parent.parent.children[1].children;

            heatMapManager.executeTaskImmediatelyAfterTime(upper, lower);
          }
        } else {
          const isLower = this.objects[0].userData["jaw"] === Jaw.Lower;

          let upper = isLower
              ? this.objects[0].parent.parent.children[0].children
              : [this.objects[0]],
            lower = isLower
              ? [this.objects[0]]
              : this.objects[0].parent.parent.children[1].children;

          heatMapManager.executeTaskImmediatelyAfterTime(upper, lower);
        }
      }
      this.isActionDone = false;
    }
  }

  getIntersects(event, objects) {
    this.updateMouseCoords(event);

    this.raycaster.setFromCamera(this.mouse, this.activeCamera);
    return this.raycaster.intersectObjects(objects);
  }

  updateGizmoPosition() {
    this.gizmoGroup.position.copy(this.object.position);
  }
  /**
   * Returns a perpendicular vector strictly within the XZ-plane.
   * @param {THREE.Vector3} vector - Input vector (must be in the XZ-plane, Y=0)
   * @returns {THREE.Vector3} - Perpendicular vector normalized
   */
  getPerpendicularXZ(vector) {
    if (vector.y !== 0) {
      console.warn("Input vector should be in the XZ-plane (y=0).");
    }
    // Perpendicular in XZ-plane is simply (-z, 0, x)
    return new THREE.Vector3(-vector.z, 0, vector.x).normalize();
  }

  setObjects(objects) {
    if (Array.isArray(objects)) {
      this.objects = objects;
    } else {
      this.objects = [objects];
    }
  }
}
